import type { FC } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { HypothesisCreativeEntry, HypothesisGroup, HypothesisSection, } from '../../types/hypothesis';
import type { HypothesisPack } from '../../hooks';
import CreativesGridSkeleton from './CreativesGridSkeleton';
import CreativesEmptyState from './CreativesEmptyState';
import CreativesGroupedView from './CreativesGroupedView';
import CreativesFlatGrid from './CreativesFlatGrid';

interface TestCreativesContentProps {
  isInitialLoading: boolean;
  hasResults: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  groupByHypothesis: boolean;
  packsByHypothesis: Map<string, HypothesisPack[]>;
  thisWeek: HypothesisGroup[];
  upcoming: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  sortedFlatCreatives: HypothesisCreativeEntry[];
  selectedCreatives: string[];
  columns: GridColDef[];
  onGroupSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange: (packIndex: number, packCreativeIds: string[]) => void;
  onFlatSelectionChange: (ids: string[]) => void;
  onSectionChange: (hypothesis: string, section: HypothesisSection) => void;
}

const TestCreativesContent: FC<TestCreativesContentProps> = ({
  isInitialLoading,
  hasResults,
  hasActiveFilters,
  onClearFilters,
  groupByHypothesis,
  packsByHypothesis,
  thisWeek,
  upcoming,
  noHypothesis,
  sortedFlatCreatives,
  selectedCreatives,
  columns,
  onGroupSelectionChange,
  onPackSelectionChange,
  onFlatSelectionChange,
  onSectionChange,
}) => {
  if (isInitialLoading) {
    return <CreativesGridSkeleton />;
  }

  if (!hasResults) {
    return (
      <CreativesEmptyState
        hasActiveFilters={hasActiveFilters}
        onClearFilters={hasActiveFilters ? onClearFilters : undefined}
      />
    );
  }

  if (groupByHypothesis) {
    return (
      <CreativesGroupedView
        thisWeek={thisWeek}
        upcoming={upcoming}
        noHypothesis={noHypothesis}
        packsByHypothesis={packsByHypothesis}
        selectedCreatives={selectedCreatives}
        columns={columns}
        onSelectionChange={onGroupSelectionChange}
        onPackSelectionChange={onPackSelectionChange}
        onSectionChange={onSectionChange}
      />
    );
  }

  return (
    <CreativesFlatGrid
      rows={sortedFlatCreatives}
      columns={columns}
      selectedCreatives={selectedCreatives}
      onSelectionChange={onFlatSelectionChange}
    />
  );
};

export default TestCreativesContent;
